import {RefObject, useEffect, useState} from 'react'

import L from '../leaflet.js'
import useLatest from './useLatest.js'

/**
 * Creates a Leaflet map in the container once it is mounted and removes it on unmount, so its layers, listeners and
 * handlers don't outlive the component. A resize of the container (a panel opened or resized next to the map)
 * updates the map's size, no caller has to call invalidateSize.
 *
 * init runs once with the new map (set the view, add tiles and controls); a function it returns runs before the map
 * is removed. Returns the map, null until it exists.
 */
export default function useLeafletMap(containerRef: RefObject<HTMLElement>, options: L.MapOptions, init?: (map: L.Map) => void | (() => void)): L.Map | null {
    const [map, setMap] = useState<L.Map | null>(null)
    const latest = useLatest({options, init})

    useEffect(() => {
        const container = containerRef.current

        if (!container) {
            return
        }

        const map = L.map(container, latest.current.options)
        // Leaflet gives the map a tabindex, so a click on it showed the browser's focus outline around the whole map.
        // The controls on the map keep their own focus ring.
        container.style.outline = 'none'
        const cleanup = latest.current.init?.(map)

        // One invalidateSize per frame, a panel being dragged resizes the map many times per frame
        let frame = 0
        const onResize = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => map.invalidateSize({pan: false}))
        }
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onResize)
        observer?.observe(container)

        setMap(map)

        return () => {
            observer?.disconnect()
            cancelAnimationFrame(frame)

            if (typeof cleanup === 'function') {
                cleanup()
            }

            map.remove()
        }
    }, [])

    return map
}
